import React, { useEffect, useRef, useState } from 'react';
import { Loader2, X, Camera } from 'lucide-react';

/**
 * In-app live camera viewfinder for receipt capture.
 *
 * Streams the rear camera into a <video> inside the dialog via getUserMedia —
 * no external camera activity ever opens, so Android cannot evict/reload the
 * app mid-capture (the failure mode that killed the capture="environment"
 * flow). Tapping the shutter draws the current frame to a canvas and hands
 * back a JPEG data URL.
 *
 * If the camera can't start (permission denied, no camera, insecure context),
 * onUnavailable fires so the caller can fall back to the OS photo picker.
 */
const ReceiptCameraView = ({ onCapture, onCancel, onUnavailable }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [ready, setReady] = useState(false);

  // Keep the latest onUnavailable without retriggering the camera effect.
  const onUnavailableRef = useRef(onUnavailable);
  onUnavailableRef.current = onUnavailable;

  useEffect(() => {
    let cancelled = false;
    if (!navigator.mediaDevices?.getUserMedia) {
      onUnavailableRef.current?.();
      return undefined;
    }
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1920 },
            height: { ideal: 1920 },
          },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setReady(true);
      } catch {
        if (!cancelled) onUnavailableRef.current?.();
      }
    })();
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, []);

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  const snap = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    // Cap the long edge so the data URL stays a sane size for scan + storage.
    const scale = Math.min(1, 2000 / Math.max(video.videoWidth, video.videoHeight));
    const c = document.createElement('canvas');
    c.width = Math.round(video.videoWidth * scale);
    c.height = Math.round(video.videoHeight * scale);
    c.getContext('2d').drawImage(video, 0, 0, c.width, c.height);
    const dataUrl = c.toDataURL('image/jpeg', 0.9);
    stopStream();
    onCapture?.(dataUrl);
  };

  return (
    <div className="space-y-3" data-testid="receipt-camera-view">
      <div className="relative rounded-md overflow-hidden border border-border bg-black">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="block w-full max-h-[55vh] object-contain bg-black"
          data-testid="receipt-camera-video"
        />
        {!ready ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-muted-foreground">
            <Loader2 size={22} className="animate-spin" />
            <span className="text-xs">Starting camera…</span>
          </div>
        ) : null}
        <button
          type="button"
          onClick={() => {
            stopStream();
            onCancel?.();
          }}
          className="absolute top-2 right-2 rounded-full bg-black/60 text-white p-2"
          aria-label="Close camera"
          data-testid="receipt-camera-cancel"
        >
          <X size={16} />
        </button>
      </div>
      <div className="flex items-center justify-center py-1">
        <button
          type="button"
          onClick={snap}
          disabled={!ready}
          className="h-16 w-16 rounded-full border-4 border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/20 flex items-center justify-center text-[hsl(var(--primary))] active:scale-95 transition-transform disabled:opacity-40"
          aria-label="Take photo"
          data-testid="receipt-camera-shutter"
        >
          <Camera size={26} />
        </button>
      </div>
      <div className="text-[11px] text-center text-muted-foreground">
        Fill the frame with the receipt, hold steady, then tap the shutter.
      </div>
    </div>
  );
};

export default ReceiptCameraView;
